/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type Maybe from 'graphql/tsutils/Maybe';

import { Button as CDSButton, Grid, Typography, Typography2, breakpoints, useTheme } from '@coursera/cds-core';
import type { ButtonProps, Theme } from '@coursera/cds-core';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import type { EntWebsitePlanCardFragment } from 'bundles/ent-website/__generated__/contentfulTypes';
import BenefitList from 'bundles/ent-website/components/BenefitList';

const styles = {
  cardContainer: () => css`
    ${breakpoints.down('sm')} {
      margin: 28px 0 0;
    }
  `,
  card: (_: Theme, card: Maybe<EntWebsitePlanCardFragment>) => css`
    height: 100%;
    box-shadow: 0 4px 4px 0 rgb(225 225 225 / 10%);
    border-top: 8px solid ${card?.topBorderColor ?? 'rgba(0, 86, 210, 1)'};
    border-radius: 8px;
    margin-inline-start: 18px;
    background-color: white;
    padding: 24px;
    display: flex;
    ${breakpoints.down('sm')} {
      margin: 0;
    }
  `,
  cardTitle: () => css`
    font-size: 32px;
    ${breakpoints.down('sm')} {
      font-size: 20px;
      line-height: 100%;
    }
  `,
  cardSubtitle: css`
    line-height: 150%;
    margin-top: 4px;
  `,
  pricingSection: () => css`
    height: 91px;
    flex-wrap: nowrap;
    ${breakpoints.down('sm')} {
      height: unset;
    }
  `,
  price: () => css`
    ${breakpoints.down('sm')} {
      font-size: 32px;
    }
  `,
  priceSubtitle: css`
    font-size: 14px;
    font-weight: 600;
    line-height: 140%;
  `,
  priceSubtitle2: (_: Theme, card: Maybe<EntWebsitePlanCardFragment>) => css`
    font-size: ${!card?.price && !card?.priceSubtitle ? 16 : 14}px;
    font-weight: 400;
    line-height: 140%;
  `,

  cta: () => css`
    ${breakpoints.down('sm')} {
      height: 40px;
    }
  `,
};
const Button = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(CDSButton);

type EntWebsitePlanCardProps = {
  card: Maybe<EntWebsitePlanCardFragment>;
};

export const EntWebsitePlanCard = ({ card }: EntWebsitePlanCardProps) => {
  const theme = useTheme();

  return (
    <Grid item sm={12} md={6} css={styles.cardContainer}>
      <div css={styles.card(theme, card)}>
        <Grid container direction="column">
          <Grid
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
            css={css`
              margin-bottom: 16px;
            `}
          >
            <Typography variant="h2semibold" css={styles.cardTitle}>
              {card?.title}
            </Typography>
            <Typography2 component="p" css={styles.cardSubtitle}>
              {card?.subtitle}
            </Typography2>
          </Grid>
          <Grid
            container
            direction="column"
            alignItems="center"
            justifyContent="flex-start"
            css={styles.pricingSection}
          >
            <Typography align="center" variant="d2semibold" css={styles.price}>
              {card?.price}
            </Typography>
            <Typography2 component="p" align="center" css={styles.priceSubtitle}>
              <strong>{card?.priceSubtitle}</strong>
            </Typography2>
            <Typography2 component="p" align="center" css={styles.priceSubtitle2(theme, card)}>
              {card?.priceSubtitle2}
            </Typography2>
          </Grid>
          <Grid
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
            css={css`
              margin: 32px 0;
            `}
          >
            {card?.cta?.ctaTarget && card?.cta?.ctaText && (
              <Button
                component="a"
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error - href does exist when component is a, something wrong with types
                href={card?.cta?.ctaTarget}
                target={card?.cta?.shouldOpenInNewWindow ? '_blank' : undefined}
                trackingName="plan_card_cta"
                css={styles.cta}
              >
                {card?.cta?.ctaText}
              </Button>
            )}
          </Grid>
          <Grid
            css={css`
              flex: 1;
            `}
          >
            <BenefitList items={card?.benefitListCollection?.items?.map((item) => item?.headline)} />
          </Grid>
          {card?.bottomCta?.ctaTarget && card?.bottomCta?.ctaText && (
            <Grid
              container
              direction="column"
              alignItems="flex-start"
              justifyContent="flex-start"
              css={css`
                margin-top: 24px;
              `}
            >
              (
              <Button
                component="a"
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error - href does exist when component is a, something wrong with types
                href={card?.bottomCta?.ctaTarget}
                target={card?.bottomCta?.shouldOpenInNewWindow ? '_blank' : undefined}
                trackingName="plan_card_bottom_cta"
                variant="ghost"
                size="small"
              >
                {card?.bottomCta?.ctaText}
              </Button>
              )
            </Grid>
          )}
        </Grid>
      </div>
    </Grid>
  );
};

export default React.memo(EntWebsitePlanCard);
